import React, { useState, useRef, useEffect } from 'react';
import { Send, AlertCircle, Mic, MicOff } from 'lucide-react';
import { VoiceRecorder } from './VoiceRecorder';
import { SimpleAudioVisualizer } from './AudioVisualizer';
import type { MessageInputProps } from '../../types/chat';
import type { AudioTranscriptionResponse } from '../../types/voice';

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
  placeholder = "Nhập tin nhắn của bạn...",
  maxCharacters = 500
}) => {
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [autoStartVoice, setAutoStartVoice] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const remainingChars = maxCharacters - message.length;
  const isNearLimit = remainingChars <= 50;
  const isAtLimit = remainingChars <= 0;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const sendMessage = () => {
    const trimmedMessage = message.trim();
    
    // Validation
    if (!trimmedMessage) {
      setError('Vui lòng nhập tin nhắn');
      return;
    }

    if (trimmedMessage.length > maxCharacters) {
      setError(`Tin nhắn quá dài. Tối đa ${maxCharacters} ký tự được phép.`);
      return;
    }

    // Clear error and send message
    setError('');
    onSendMessage(trimmedMessage);
    setMessage('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter to send (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    
    // Enforce character limit
    if (newValue.length <= maxCharacters) {
      setMessage(newValue);
    }
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  // Handle voice recording toggle
  const handleVoiceToggle = () => {
    setShowVoiceRecorder(prev => {
      const next = !prev;
      setAutoStartVoice(next);
      return next;
    });
    setError('');
  };



  // Handle transcription completion
  const handleTranscriptionComplete = (response: AudioTranscriptionResponse) => {
    setIsTranscribing(false);
    setShowVoiceRecorder(false);
    setAutoStartVoice(false);
    
    if (response.success && response.data.text) {
      // Add transcribed text to current message
      const combined = message ? `${message} ${response.data.text}` : response.data.text;
      if (combined.length <= maxCharacters) {
        setMessage(combined);
        setError('');
      } else {
        setMessage(combined.slice(0, maxCharacters));
        setError(`Văn bản chuyển đổi vượt quá giới hạn ${maxCharacters} ký tự, đã cắt bớt.`);
      }
    } else {
      setError(response.error || 'Không thể chuyển đổi giọng nói thành văn bản');
    }
  };

  // Handle transcription error
  const handleTranscriptionError = (error: Error) => {
    setIsTranscribing(false);
    setError(`Lỗi ghi âm: ${error.message}`);
  };

  // Handle voice recording cancel
  const handleVoiceCancel = () => {
    setShowVoiceRecorder(false);
    setIsTranscribing(false);
    setAutoStartVoice(false);
    setError('');
  };

  return (
    <div className="border-t border-gray-200 bg-white p-4">
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Error message */}
        {error && (
          <div 
            className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded-lg"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Voice Recorder */}
        {showVoiceRecorder && (
          <div className="mb-4">
          <VoiceRecorder
            onTranscriptionComplete={handleTranscriptionComplete}
            onError={handleTranscriptionError}
            onCancel={handleVoiceCancel}
            disabled={disabled || isTranscribing}
            autoStart={autoStartVoice}
            className="border border-gray-200 rounded-lg"
          />
          </div>
        )}

        {/* Input container */}
        <div className="flex items-center space-x-2">
          {/* Voice button */}
          <button
            type="button"
            onClick={handleVoiceToggle}
            disabled={disabled || isTranscribing}
            className={`
              flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
              transition-all duration-200 transform
              ${showVoiceRecorder
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
              }
              ${disabled || isTranscribing ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
            `}
            aria-label={showVoiceRecorder ? 'Đóng ghi âm' : 'Mở ghi âm'}
            title={showVoiceRecorder ? 'Đóng ghi âm' : 'Ghi âm tin nhắn'}
          >
            {showVoiceRecorder ? (
              <MicOff className="w-5 h-5" />
            ) : (
              <Mic className="w-5 h-5" />
            )}
          </button>



          {/* Textarea container */}
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={isTranscribing ? 'Đang chuyển đổi giọng nói...' : placeholder}
              disabled={disabled || isTranscribing}
              rows={1}
              className={`
                w-full px-4 py-3 pr-16 border rounded-2xl resize-none
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-base leading-relaxed
                transition-all duration-200
                ${error ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'}
                min-h-[52px] max-h-[120px]
              `}
              aria-label="Nhập tin nhắn"
              aria-describedby={error ? 'input-error' : 'char-count'}
            />
            
            {/* Character count */}
            <div 
              id="char-count"
              className={`
                absolute bottom-2 right-2 text-xs px-2 py-1 rounded
                ${isAtLimit 
                  ? 'text-red-600 bg-red-100' 
                  : isNearLimit 
                    ? 'text-orange-600 bg-orange-100' 
                    : 'text-gray-500 bg-gray-100'
                }
                transition-colors duration-200
              `}
              aria-live="polite"
            >
              {remainingChars}
            </div>
          </div>

          {/* Send button container */}
          <div className="flex items-center justify-center">
            <button
              type="submit"
              disabled={disabled || !message.trim() || isAtLimit || isTranscribing}
              className={`
                flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center
                transition-all duration-200 transform
                ${disabled || !message.trim() || isAtLimit || isTranscribing
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 active:scale-95 hover:shadow-lg'
                }
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                shadow-lg border-2 border-white
              `}
              aria-label="Gửi tin nhắn"
              title={
                disabled 
                  ? 'Đang gửi...' 
                  : isTranscribing
                    ? 'Đang chuyển đổi giọng nói...'
                  : !message.trim() 
                    ? 'Nhập tin nhắn để gửi' 
                    : isAtLimit
                      ? 'Tin nhắn quá dài'
                      : 'Gửi tin nhắn (Enter)'
              }
            >
              <Send 
                className={`w-6 h-6 ${
                  disabled || !message.trim() || isAtLimit || isTranscribing ? 'text-gray-500' : 'text-white'
                }`} 
              />
            </button>
          </div>
        </div>

        {/* Keyboard shortcut hint */}
        <div className="text-xs text-gray-500 text-center">
          <div className="flex items-center justify-center space-x-4">
            <span>
              Nhấn <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Enter</kbd> để gửi, 
              <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs ml-1">Shift + Enter</kbd> để xuống dòng
            </span>
            <span className="text-gray-400">•</span>
            <span>
              <Mic className="w-3 h-3 inline mr-1" />
              Nhấn biểu tượng mic để ghi âm
            </span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MessageInput;
